import React, { useCallback, useContext } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import orderBy from 'lodash/orderBy';
import uniq from 'lodash/uniq';

import { getWorkspaceDisplayLabel } from 'Shared/selectors/workspace';
import { personalWorkspaceName } from 'Shared/constants/constants';
import { useDeepEqualSelector, useQueryParam } from 'Shared/utils/hooks';
import { sanitizeFullName } from 'Shared/helpers';
import ExpandNavbarContext from 'Shared/contexts/ExpandNavbarContext';
import QueryParamConstants from 'Shared/constants/QueryParams.constants';

import AddTeammateModalHandler from './AddTeammateModalHandler/AddTeammateModalHandler';
import { fakeNudgebotData, getTeammateItemAvatar } from './utils';
import TeamGroupTemplate from '../TeamGroupTemplate/TeamGroupTemplate';
import TeamItem from '../TeamItem/TeamItem';
import EmptySectionState from '../EmptySectionState/EmptySectionState';
import { WpTooltip, WpTooltipContent, WpTooltipTrigger } from 'Wpds/components/tooltip';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';

type Props = {
  showFeature: Function;
  onCloseNavbar: Function;
};

function TeamSection({ showFeature, onCloseNavbar }: Props) {
  const isExpanded = useContext(ExpandNavbarContext);
  const {
    id: currentUserId,
    avatar,
    user: { skipedMembersIds },
  } = useCurrentUser();
  const { showAllUsersInSidebar } = useFlags();

  const personalWorkspace = useDeepEqualSelector(state => {
    const { workspaces } = state;
    if (!workspaces?.length) return null;
    const workspace = workspaces.find(({ isPersonal, starred }) => isPersonal && !starred);
    if (!workspace) return null;
    return {
      id: workspace.id,
      isPersonal: true,
      displayName: personalWorkspaceName,
      displayAvatar: avatar,
      isNotInvited: false,
      isInvitePending: false,
      path: workspace.path,
      starred: workspace.starred,
    };
  });

  const workspaces = useDeepEqualSelector(state =>
    state.workspaces.filter(({ starred, isPersonal, isGroup }) => !starred && !isPersonal && !isGroup)
  );

  const orgMembersWithoutWorkspaces = useDeepEqualSelector(state => {
    if (!showAllUsersInSidebar) return [];
    const { members } = state;
    const teammatesIds = uniq(workspaces.map(({ users }) => users.map(({ id }) => id)).flat()).filter(
      ({ id }) => id !== currentUserId
    );
    const membersWithoutWorkspaces = members.filter(
      ({ id }) => !teammatesIds.includes(id) && !skipedMembersIds.includes(id)
    );
    return membersWithoutWorkspaces.map(member => {
      const displayName = sanitizeFullName(member.fullName) || member.email;
      const isInvitePending = Boolean((!member.confirmedAt && member.confirmationSentAt) || !member.lastSeenAt);
      return {
        id: null,
        memberId: member.id,
        displayName,
        displayAvatar: getTeammateItemAvatar({ user: member }),
        isNotInvited: false,
        isInvitePending,
        path: `/workspaces/accept_member/${member.slug}`,
        notificationsCount: 0,
      };
    });
  });

  const teammatesWorkspaces = useDeepEqualSelector(state => {
    return workspaces.map(({ users, id, path, workspaceNotifications, temporaryAvatarUrl, starred }) => {
      const restUser = users.find(({ id: userId }) => userId !== currentUserId);
      const displayName = getWorkspaceDisplayLabel(id)(state);
      const isInvitePending = Boolean(
        restUser && ((!restUser.confirmedAt && restUser.confirmationSentAt) || !restUser.lastSeenAt)
      );
      const showNewBadge = !workspaceNotifications.viewedWorkspace;

      return {
        id,
        displayName,
        displayAvatar: getTeammateItemAvatar({ user: restUser, avatarUrl: temporaryAvatarUrl }),
        isNotInvited: Boolean(!restUser),
        isInvitePending,
        path,
        notificationsCount: workspaceNotifications.count,
        showNewBadge,
        starred: starred,
      };
    });
  });

  const orderedList = orderBy(
    [...teammatesWorkspaces, ...orgMembersWithoutWorkspaces],
    ({ displayName }) => displayName
  );
  const nudgeBotLink = showFeature('recommendations') && fakeNudgebotData;

  const teamList = [personalWorkspace, nudgeBotLink, ...orderedList].filter(n => n);

  const [, setAddTeammateModal] = useQueryParam(QueryParamConstants.ADD_TEAMMATE_MODAL);

  const handleAddTeammateOpen = useCallback(() => {
    (window as any).analytics.track('Clicked Add Workspace', { isGroup: false, isMultiple: false, source: 'sidebar' });
    setAddTeammateModal(true);
  }, [setAddTeammateModal]);

  return (
    <div className="w-100">
      <AddTeammateModalHandler onRedirectTo={() => setAddTeammateModal(null)} />
      <TeamGroupTemplate
        title={
          <WpTooltip placement="top">
            <WpTooltipTrigger>
              <div className="d-flex align-items-center">1:1 Workspaces</div>
            </WpTooltipTrigger>
            <WpTooltipContent>
              Use 1:1 workspaces to privately collaborate on meetings agendas and action items with one person{' '}
            </WpTooltipContent>
          </WpTooltip>
        }
        tooltipLabel="Add 1:1 Workspace"
        addButtonLabel="Add 1:1 Workspace"
        onAddClick={handleAddTeammateOpen}
        onCloseNavbar={onCloseNavbar}
      >
        {teamList.map(
          ({
            id,
            memberId,
            isPersonal,
            displayName,
            displayAvatar,
            isNotInvited,
            isInvitePending,
            path,
            notificationsCount,
            showNewBadge,
            starred,
          }) => (
            <TeamItem
              key={id || memberId}
              isPersonal={isPersonal}
              displayName={displayName}
              displayAvatar={displayAvatar}
              path={path}
              isNotInvited={isNotInvited}
              isInvitePending={isInvitePending}
              notificationsCount={notificationsCount}
              onCloseNavbar={onCloseNavbar}
              showNewBadge={showNewBadge}
              workspaceId={id}
              starred={starred}
            />
          )
        )}
        {Boolean(!orderedList.length) && isExpanded && (
          <EmptySectionState>Get started by adding your reports</EmptySectionState>
        )}
      </TeamGroupTemplate>
    </div>
  );
}

export default TeamSection;
